import AircraftCard from "../quote/AircraftCard";
import React, { useCallback, useContext, useRef, useState, useEffect } from "react";
import useClickOutside from "../../hooks/useClickOutside";
import QuoteHeader from "../quote/QuoteHeader";
import useGet from "../../hooks/useGet";
import LoadingOverlay from "../shared/LoadingOverlay";
import { useNavigate } from "react-router-dom";
import ErrorModal from "../shared/ErrorModal";
import usePost from "../../hooks/usePost";
import SEO from "../shared/SEO";
import PortalQuoteFeedbackDrawer from "./PortalQuoteFeedbackDrawer";
import PortalPricesEmbed from "./PortalPricesEmbed";
import Button from "../ui/Button";
import chatIcon from "../../assets/icons/chat-gold.svg";
import AuthContext from "../../store/AuthContext";
import PortalContext from "../../store/PortalContext";
import { createBrokerAction, fetchBrokerActions } from "../../services/brokerActions";
import { BROKER_REVIEW_STAGE, canonicalStage } from "../../services/brokerReview";
import {
    applyPricingEdit,
    optionFromAircraft,
    releaseQuoteToCustomer,
    storeQuotePrices,
} from "../../services/quotePrices";

import ReturnMessage from "../shared/ReturnMessage";

const EDITABLE_STAGES = ["Out to market", BROKER_REVIEW_STAGE, "Quoted"];
const FEEDBACK_STAGES = [BROKER_REVIEW_STAGE, "Quoted"];
const PRE_RELEASE_STAGES = ["Out to market", BROKER_REVIEW_STAGE];

const PortalQuoteOptions = ({
    deal,
    onDidReserveResult,
    onClose,
    anchorOnLoad = false,
    quoteSettings,
    showMovida,
    settingsBusy = false,
}) => {
    const legId = deal.daM_Ref;
    const stage = canonicalStage(deal.stage);
    const canEdit = EDITABLE_STAGES.includes(stage);
    const canRaiseFeedback = FEEDBACK_STAGES.includes(stage);
    const canReserve = !PRE_RELEASE_STAGES.includes(stage);
    const authCtx = useContext(AuthContext);
    const portalCtx = useContext(PortalContext);

    // Hooks
    const navigate = useNavigate();
    const { postData, sending, returnMessage } = usePost();

    // State
    const [aircraftData, setAircraftData] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [showCompareBar] = useState(false);
    const [compareSelection, setCompareSelection] = useState([]);
    const [, setFilterScrollPosition] = useState("");
    const [expanded, setExpanded] = useState(null);
    const [dimAircraft, setDimAircraft] = useState(null);
    const [exitAircraft] = useState(false);
    const [enterAircraft] = useState(false);
    const [loading, setLoading] = useState(true);
    const [getSearchResultsApi, setGetSearchResultsApi] = useState(false);
    const [searchResultsHeaderApi, setSearchResultsHeaderApi] = useState(false);
    const [sharedDataApi] = useState(false);
    const [error, setError] = useState(false);
    const [thumbs, setThumbs] = useState([]);
    const [sharedMessage] = useState(null);
    const [optionStates, setOptionStates] = useState({});
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [feedbackNotes, setFeedbackNotes] = useState([]);
    const [feedbackLoading, setFeedbackLoading] = useState(false);
    const [releasing, setReleasing] = useState(false);
    const [actionError, setActionError] = useState("");
    const anchoredRef = useRef(false);
    const savingOptionRef = useRef(false);
    const [savingOption, setSavingOption] = useState(false);
    const [savingRecommendedTail, setSavingRecommendedTail] = useState(null);

    // Get quote data
    const {
        loading: searchResultsLoading,
        error: searchError,
        returnData: searchResults,
    } = useGet(getSearchResultsApi);
    const {
        loading: searchResultsHeaderLoading,
        error: headerError,
        returnData: searchResultsHeader,
    } = useGet(searchResultsHeaderApi);
    const { returnData: sharedData } = useGet(sharedDataApi);

    // Use Effect
    useEffect(() => {
        // Set endpoint apis
        setGetSearchResultsApi(`/api/frontend/v2/get-search-results?LegId=${legId}`);
        setSearchResultsHeaderApi(`/api/frontend/get-search-results-header?LegId=${legId}`);

        // Redirect if approved flights have successfully been submitted
        if (returnMessage.success && returnMessage.postRef === "postApproval") {
            //navigate('/approve-flights-success')
            //alert('aircraft reserved')
        }

        // Handle successful reservation
        if (returnMessage.success && returnMessage.postRef === "postReserve") {
            // Close the modal and reload to show updated deal stage
            if (onDidReserveResult) {
                onDidReserveResult();
            }
            // Reload the page to refresh the deal stage
            window.location.reload();
        }

        if (searchResultsHeader.expired || sharedData.sharedQuoteIsExpired) {
            setError("The page you have clicked to view has now expired or you have already completed this step.");

            setLoading(false);
        }

        // Quote data has loaded
        if (!searchResultsLoading && !searchResultsHeaderLoading && searchResults && searchResultsHeader) {
            if (searchResults[0]) {
                if (!aircraftData) {
                    resetAircraftData(searchResults);
                }
            } else {
                setError(
                    "Sorry, no quote was found. It may be that your broker hasn't put it together yet, please check back in a few hours. Alternatively you may be signed in with a different account."
                );
            }

            setLoading(false);
        }

        // Show server errors
        if (searchError || headerError) {
            setError(headerError ? headerError : searchError);
            setLoading(false);
        }

        // Set share message

        // Listen for window resize events
        let resizeTimeout;

        const resizeHandler = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                // setSize({
                //     width: window.innerWidth,
                //     height: window.innerHeight,
                // })
            }, 100);
        };

        resizeHandler();
        window.addEventListener("resize", resizeHandler);

        return () => {
            window.removeEventListener("resize", resizeHandler);
        };
        // resetAircraftData is a local closure defined below; including it
        // would re-fire the effect on every render. Real triggers are listed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        aircraftData,
        headerError,
        legId,
        navigate,
        onDidReserveResult,
        returnMessage,
        searchError,
        searchResults,
        searchResultsHeader,
        searchResultsHeaderLoading,
        searchResultsLoading,
        sharedData,
    ]);

    const resetAircraftData = (data) => {
        if (sharedData) {
            if (sharedData.sharedFlights) {
                if (sharedData.sharedFlights.length > 0) {
                    let tailNumbers = [];

                    sharedData.sharedFlights.forEach((flight) => {
                        tailNumbers.push(flight.tailNumber);
                    });

                    data = data.filter((item) => {
                        return tailNumbers.indexOf(item.tailNumber) > -1;
                    });
                }
            }
        }

        setAircraftData(data);
    };

    // Expand more / less handler
    const expandedHandler = (aircraftId) => {
        if (aircraftId === expanded) {
            setExpanded(false);
            setDimAircraft(false);
        } else {
            setExpanded(aircraftId);
            setDimAircraft(aircraftId);
        }
    };

    // Thumbs up
    const thumbsHandler = (status, tailNumber) => {
        let updatedSelection = thumbs;
        if (!thumbs.includes(tailNumber)) {
            setThumbs([...updatedSelection, tailNumber]);
        } else {
            setThumbs(updatedSelection.filter((selected) => selected !== tailNumber));
        }
    };

    useEffect(() => {
        if (!aircraftData) return;
        setOptionStates(Object.fromEntries(aircraftData.map((a) => [a.tailNumber, optionFromAircraft(a)])));
    }, [aircraftData]);

    const loadFeedback = useCallback(async () => {
        if (!deal?.id) return;
        setFeedbackLoading(true);
        try {
            setFeedbackNotes(await fetchBrokerActions(authCtx?.token, deal.id));
        } catch {
            setActionError("Could not load feedback for this deal.");
        } finally {
            setFeedbackLoading(false);
        }
    }, [authCtx?.token, deal?.id]);

    useEffect(() => {
        loadFeedback();
    }, [loadFeedback]);

    const handleAddFeedback = async (text) => {
        await createBrokerAction({ dealId: deal.id, salesBrokerId: portalCtx?.id, text }, authCtx?.token);
        await loadFeedback();
    };

    const unresolvedFeedback = feedbackNotes.filter((note) => !note.resolved).length;
    const shownCount = aircraftData ? aircraftData.filter((a) => !optionStates[a.tailNumber]?.hidden).length : 0;

    useEffect(() => {
        if (!anchorOnLoad || anchoredRef.current || loading || !aircraftData) return;
        const el = document.getElementById("quote-section");
        if (!el) return;
        anchoredRef.current = true;
        requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    }, [anchorOnLoad, loading, aircraftData]);

    const runAction = useCallback(async (action, message) => {
        setActionError("");
        try {
            return await action();
        } catch (e) {
            setActionError(message);
            throw e;
        }
    }, []);

    // The endpoint stores every option on the quote, so each change sends the full set.
    const saveOption = async (tailNumber, nextOption) => {
        if (savingOptionRef.current) throw new Error("An option is already being saved");
        savingOptionRef.current = true;
        setSavingOption(true);
        try {
            const next = { ...optionStates, [tailNumber]: nextOption };
            await storeQuotePrices(
                {
                    legId,
                    memberId: deal.memberId,
                    options: aircraftData.map((a) => next[a.tailNumber]).filter(Boolean),
                },
                authCtx?.token
            );
            setOptionStates(next);
        } finally {
            savingOptionRef.current = false;
            setSavingOption(false);
        }
    };

    const updateOption = (tailNumber, patch, errorMessage) =>
        runAction(() => saveOption(tailNumber, { ...optionStates[tailNumber], ...patch }), errorMessage).catch(
            () => {}
        );

    const toggleHidden = (tailNumber) =>
        updateOption(
            tailNumber,
            { hidden: !optionStates[tailNumber]?.hidden },
            "Could not update the aircraft option. Please try again."
        );

    const toggleRecommended = async (tailNumber, recommended) => {
        if (savingOptionRef.current) return;
        setSavingRecommendedTail(tailNumber);
        try {
            await updateOption(tailNumber, { recommended }, "Could not update SHY Recommended. Please try again.");
        } finally {
            setSavingRecommendedTail(null);
        }
    };

    const savePricing = (tailNumber, values) =>
        saveOption(tailNumber, applyPricingEdit(optionStates[tailNumber], values));

    const handleReleaseToCustomer = async () => {
        setReleasing(true);
        try {
            await runAction(
                () => releaseQuoteToCustomer(legId, authCtx?.token),
                "Could not release the quote. Please try again."
            );
            // Same as reserving: reload so the deal comes back from the CRM at Quoted.
            window.location.reload();
        } catch {
            setReleasing(false);
        }
    };

    // Filter and animation handlers removed as filters UI is not currently used.

    // Detect click outside of filters
    const filtersButtonRef = useRef(null);
    const filterBarRef = useRef(null);
    useClickOutside([filtersButtonRef, filterBarRef], () => {
        if (showFilters) {
            setShowFilters(false);
        }
    });

    // Detect click outside of expanded tray
    const cardRef = useRef(null);
    useClickOutside([cardRef], () => {
        setExpanded(false);
        setDimAircraft(false);
    });

    // Handle the current selection to be compared
    const compareSelectionHandler = (value, id) => {
        let updatedSelection = compareSelection;
        if (value === true) {
            setCompareSelection([...updatedSelection, id]);
        } else {
            setCompareSelection(updatedSelection.filter((selected) => selected !== id));
        }
    };

    // Compare and thumbs bulk handlers removed as they are not currently used.

    const onPortalBookingHandler = (arfq, tailNum, currency) => {
        // Validate currency is selected
        if (!currency) {
            setError("Please select a currency before reserving the quote.");
            return;
        }

        let formData = {
            MemberId: deal.memberId,
            currency: currency,
            legid: legId,
            arfq: arfq,
            tailNumber: tailNum,
        };

        // Post
        postData(formData, "/api/frontend/add-booking-to-user", 1000, "postReserve");
    };

    return (
        <>
            <SEO title="Quote" />
            <div className="quote">
                {loading || sending ? (
                    <LoadingOverlay clear={true} />
                ) : error ? (
                    <ErrorModal errorMessage={error} />
                ) : (
                    <>
                        <ReturnMessage inline returnMessage={returnMessage} />

                        {sharedMessage && !returnMessage.error && (
                            <div id="header-message" className="quote__header-message">
                                {sharedMessage}
                            </div>
                        )}

                        <QuoteHeader
                            data={searchResultsHeader}
                            filterScrollPosition={(position) => setFilterScrollPosition(position)}
                        />

                        {aircraftData && (
                            <div id="quote-section" className="quote__results-bar wrap wrap--sm-no-padding">
                                <div className="quote__results-count">
                                    {shownCount} quote{shownCount === 1 ? "" : "s"} available
                                </div>
                                <div className="quote__results-actions">
                                    {quoteSettings?.({ disabled: savingOption || releasing })}
                                    <button
                                        type="button"
                                        className="quote__feedback-btn"
                                        onClick={() => setFeedbackOpen(true)}
                                    >
                                        <img src={chatIcon} alt="" />
                                        Feedback
                                        {unresolvedFeedback > 0 && (
                                            <span className="quote__feedback-count">{unresolvedFeedback}</span>
                                        )}
                                    </button>
                                    {stage === BROKER_REVIEW_STAGE && (
                                        <Button
                                            classNames={["button button--primary quote__release-btn"]}
                                            onClick={handleReleaseToCustomer}
                                            disabled={releasing || savingOption || settingsBusy}
                                        >
                                            {releasing ? "Releasing…" : "Release to Customer"}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        {actionError && (
                            <p className="quote__action-error wrap wrap--sm-no-padding" role="alert">
                                {actionError}
                            </p>
                        )}

                        <>
                            <div className="wrap wrap--sm-no-padding">
                                <div className="quote__aircraft-grid">
                                    {aircraftData?.length === 0 && (
                                        <p className="quote__no-results">No results matching your search</p>
                                    )}
                                    {(aircraftData || []).map((airCraft) => {
                                        const option = optionStates[airCraft.tailNumber];
                                        const saleCode = String(
                                            airCraft.preferredCurrency || airCraft.operatorCurrency || ""
                                        ).toLowerCase();
                                        return (
                                            <React.Fragment key={airCraft.tailNumber}>
                                                <AircraftCard
                                                    key={airCraft.tailNumber}
                                                    ref={airCraft.tailNumber === expanded ? cardRef : null}
                                                    id={airCraft.id}
                                                    legId={legId}
                                                    recommended={option ? option.recommended : airCraft.recommended}
                                                    category={airCraft.category}
                                                    aircraftName={airCraft.aircraftName}
                                                    photoData={airCraft.photoData}
                                                    seats={airCraft.seats}
                                                    departureTime={airCraft.departureTime}
                                                    arrivalTime={airCraft.arrivalTime}
                                                    headRoom={airCraft.headRoom}
                                                    refurbishedYear={airCraft.refurbishedYear}
                                                    cabinWidth={airCraft.cabinWidth}
                                                    cateringOption={airCraft.cateringOption}
                                                    luggage={airCraft.luggage}
                                                    toilet={airCraft.toilet}
                                                    ownerApproval={airCraft.ownerApproval}
                                                    wifiOption={airCraft.wifiOption}
                                                    co2={airCraft.co2}
                                                    yom={airCraft.yom}
                                                    price={airCraft.price}
                                                    currency={airCraft.preferredCurrency}
                                                    operatorCurrency={airCraft.operatorCurrency}
                                                    expandedId={expanded}
                                                    expandedHandler={() => expandedHandler(airCraft.tailNumber)}
                                                    dimAircraft={dimAircraft && dimAircraft !== airCraft.tailNumber}
                                                    exitAircraft={exitAircraft}
                                                    enterAircraft={enterAircraft}
                                                    thumbsStatus={thumbs.includes(airCraft.tailNumber)}
                                                    updateThumbsStatus={(status) =>
                                                        thumbsHandler(status, airCraft.tailNumber)
                                                    }
                                                    tailNumber={airCraft.tailNumber}
                                                    showCheckbox={showCompareBar}
                                                    compareSelectionHandler={(value) =>
                                                        compareSelectionHandler(
                                                            value,
                                                            `${airCraft.id}___${airCraft.tailNumber}`
                                                        )
                                                    }
                                                    compareSelected={compareSelection.includes(
                                                        `${airCraft.id}___${airCraft.tailNumber}`
                                                    )}
                                                    isPortalPage={true}
                                                    showMovida={showMovida ?? searchResultsHeader?.showMovida}
                                                    aircraftData={airCraft}
                                                    tripInfo={searchResultsHeader}
                                                    portalOption={{
                                                        costAmount: option?.costPrice,
                                                        operatorCode: String(
                                                            airCraft.operatorCurrency || ""
                                                        ).toLowerCase(),
                                                        saleCode,
                                                        pricing: option,
                                                        canEdit:
                                                            canEdit &&
                                                            !!option &&
                                                            !savingOption &&
                                                            !settingsBusy &&
                                                            !releasing,
                                                        onSavePricing: (values) =>
                                                            savePricing(airCraft.tailNumber, values),
                                                        isHidden: !!option?.hidden,
                                                        onToggleHidden: () => toggleHidden(airCraft.tailNumber),
                                                        isRecommended: option
                                                            ? option.recommended
                                                            : !!airCraft.recommended,
                                                        savingRecommended:
                                                            savingRecommendedTail === airCraft.tailNumber,
                                                        onToggleRecommended: (recommended) =>
                                                            toggleRecommended(airCraft.tailNumber, recommended),
                                                        canReserve,
                                                        onReserve: () =>
                                                            onPortalBookingHandler(
                                                                airCraft.id,
                                                                airCraft.tailNumber,
                                                                saleCode.toUpperCase()
                                                            ),
                                                    }}
                                                />
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>

                            <PortalPricesEmbed legId={legId} />
                        </>
                    </>
                )}
            </div>
            <PortalQuoteFeedbackDrawer
                open={feedbackOpen}
                onClose={() => setFeedbackOpen(false)}
                deal={deal}
                quoteHeader={searchResultsHeader}
                notes={feedbackNotes}
                loading={feedbackLoading}
                canAdd={canRaiseFeedback && !!portalCtx?.id}
                onAdd={handleAddFeedback}
            />
        </>
    );
};

export default PortalQuoteOptions;
